import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Layout from "antd/es/layout";
import { useHistory } from "react-router-dom";

import { EntityTypeSupportingReports } from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import useStateWithDebouncedListener from "@mapmycustomers/shared/util/hook/useStateWithDebouncedListener";

import Path from "@app/enum/Path";
import { getMe } from "@app/store/iam";
import { getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import Iam from "@app/types/Iam";
import Report from "@app/types/Report";
import getFeaturePathByEntityType from "@app/util/analytic/getFeaturePathByEntityType";

import { reportsExportsAnalytics } from "../../reportsAnalytics";
import {
  cloneReport,
  createReport,
  deleteReport,
  downloadReport,
  editReport,
  fetchExportCardData,
  scheduleReport,
  updateReport,
} from "../../store/actions";
import ExportCardData from "../../store/ExportCardData";
import { getExportCardData, isLoading } from "../../store/selectors";

import EditScheduleModal from "./EditScheduleModal";
import styles from "./ExportCard.module.scss";
import FilterType from "./FilterType";
import Header from "./Header";
import ReportTable from "./ReportTable";

interface Props {
  createReport: typeof createReport;
  editReport: typeof editReport;
  exportCardData: ExportCardData;
  fetchExportCardData: typeof fetchExportCardData.request;
  loading: boolean;
  me: Iam;
  onCloneReport: typeof cloneReport.request;
  onDeleteReport: typeof deleteReport.request;
  onDownloadReport: typeof downloadReport.request;
  onScheduleReport: typeof scheduleReport.request;
  onUpdateReport: typeof updateReport.request;
  users: User[];
}

const ExportCard: React.FC<Props> = ({
  createReport,
  editReport,
  exportCardData,
  fetchExportCardData,
  loading,
  me,
  onCloneReport,
  onDeleteReport,
  onDownloadReport,
  onScheduleReport,
  onUpdateReport,
  users,
}) => {
  const history = useHistory();

  const [entityTypeFilter, setEntityTypeFilter] = useState<
    EntityTypeSupportingReports | undefined
  >();
  const [typeFilter, setTypeFilter] = useState<FilterType>(FilterType.ALL);
  const [selectedReport, setSelectedReport] = useState<Report | undefined>();
  const [selectedUserIds, setSelectedUserIds] = useState<Array<User["id"]>>([]);

  useChangeTracking(
    () =>
      reportsExportsAnalytics.clicked([
        "Entity",
        ...getFeaturePathByEntityType(entityTypeFilter, ["All"]),
      ]),
    [entityTypeFilter]
  );

  useChangeTracking(
    () =>
      reportsExportsAnalytics.clicked([
        typeFilter === FilterType.ALL
          ? "Show All"
          : typeFilter === FilterType.FAVORITE
          ? "Show Favorite"
          : "Show Downloaded",
      ]),
    [typeFilter]
  );

  const handleCreateReportClick = useCallback(() => {
    createReport();
    reportsExportsAnalytics.clicked(["Create Report"]);
  }, [createReport]);

  const handleDownload = useCallback(
    (report: Report) => {
      onDownloadReport(report);
      reportsExportsAnalytics.clicked(["Table", "Download Report"], { report });
    },
    [onDownloadReport]
  );

  const handleClone = useCallback(
    (report: Report) => {
      onCloneReport({
        callback: (report) => history.push(`${Path.REPORTS}/exports/${report.id}`),
        report,
      });
      reportsExportsAnalytics.clicked(["Table", "Clone Report"], { report });
    },
    [onCloneReport, history]
  );

  const handleDelete = useCallback(
    (report: Report) => {
      onDeleteReport(report);
      reportsExportsAnalytics.clicked(["Table", "Delete Report"], { report });
    },
    [onDeleteReport]
  );

  const handleEdit = useCallback(
    (report: Report) => {
      editReport(report);
      reportsExportsAnalytics.clicked(["Table", "Edit Report"], { report });
    },
    [editReport]
  );

  const handleSchedule = useCallback(
    (report: Report) => {
      setSelectedReport(report);
    },
    [setSelectedReport]
  );

  const handleScheduleSubmit = useCallback<
    (report: Omit<Report, "frequency" | "nextRunAt" | "sendOptions"> & Partial<Report>) => void
  >(
    (report) => {
      if (!selectedReport) {
        return;
      }
      onScheduleReport(report);
      setSelectedReport(undefined);
    },
    [selectedReport, onScheduleReport]
  );

  const handleToggleFavorite = useCallback(
    (report: Report) => {
      onUpdateReport({ ...report, isStarred: !report.isStarred });
    },
    [onUpdateReport]
  );

  const [openModal, showModal, hideModal] = useBoolean();

  const handleAfterModalClose = useCallback(
    () => setSelectedReport(undefined),
    [setSelectedReport]
  );

  const updateFilters = useCallback(
    (value: string) => fetchExportCardData({ search: value.length ? value : undefined }),
    [fetchExportCardData]
  );
  const [search, setSearch] = useStateWithDebouncedListener([updateFilters, 500], "");
  const {
    reportListViewState: { range },
    total,
  } = exportCardData;
  const page = range.startRow / (range.endRow - range.startRow);

  useEffect(() => {
    fetchExportCardData({
      entityType: entityTypeFilter,
      filterType: typeFilter,
      userIds: selectedUserIds,
    });
  }, [entityTypeFilter, fetchExportCardData, typeFilter, selectedUserIds]);

  useEffect(() => {
    if (selectedReport) {
      showModal();
    }
  }, [selectedReport, showModal]);

  return (
    <Layout className={styles.container}>
      <Layout.Content className={styles.content}>
        <Header
          entityType={entityTypeFilter}
          filterType={typeFilter}
          onChangeEntityType={setEntityTypeFilter}
          onChangeFilterType={setTypeFilter}
          onCreate={handleCreateReportClick}
          onSearch={setSearch}
          onSelectUsers={setSelectedUserIds}
          reportCount={exportCardData.total}
          search={search}
          selectedUserIds={selectedUserIds}
          users={users}
        />

        <ReportTable
          fetchExportCardData={fetchExportCardData}
          loading={loading}
          onClone={handleClone}
          onDelete={handleDelete}
          onDownload={handleDownload}
          onEdit={handleEdit}
          onSchedule={handleSchedule}
          onToggleFavorite={handleToggleFavorite}
          page={page}
          reports={exportCardData.reports}
          total={total}
        />

        <EditScheduleModal
          afterClose={handleAfterModalClose}
          key={selectedReport?.id}
          me={me}
          onClose={hideModal}
          onSubmit={handleScheduleSubmit}
          open={openModal}
          report={selectedReport}
          users={users}
        />
      </Layout.Content>
    </Layout>
  );
};

const mapStateToProps = (state: RootState) => ({
  exportCardData: getExportCardData(state),
  loading: isLoading(state),
  me: getMe(state)!,
  users: getUsers(state),
});

const mapDispatchToProps = {
  createReport,
  editReport,
  fetchExportCardData: fetchExportCardData.request,
  onCloneReport: cloneReport.request,
  onDeleteReport: deleteReport.request,
  onDownloadReport: downloadReport.request,
  onScheduleReport: scheduleReport.request,
  onUpdateReport: updateReport.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ExportCard);
